// OKLab and its polar form OKLCh, which the browser cannot convert to.
//
// The space stone colours are measured and compared in: a straight distance between two OKLab
// colours is close to how different they look, which is why the colour search ranks on it. Here it
// is only needed to draw a colour — the picker paints its area and reads a choice back out.

export type OKLCh = { l: number; c: number; h: number };

// The largest chroma sRGB can show at all, reached at magenta. The picker's chroma axis runs to it,
// so the same position means the same colourfulness whatever the hue is.
export const MAX_CHROMA = 0.32249;

const toLinear = (value: number) =>
  value <= 0.04045 ? value / 12.92 : Math.pow((value + 0.055) / 1.055, 2.4);

const toSRGB = (value: number) =>
  value <= 0.0031308
    ? value * 12.92
    : 1.055 * Math.pow(Math.abs(value), 1 / 2.4) - 0.055;

export const clamp01 = (value: number) => Math.min(1, Math.max(0, value));

// Björn Ottosson's matrices, multiplied out. The channels come back unclipped, so a value outside
// 0..1 is exactly what "outside the gamut" means.
export function oklabToRGB(l: number, a: number, b: number) {
  const long = l + 0.3963377774 * a + 0.2158037573 * b;
  const medium = l - 0.1055613458 * a - 0.0638541728 * b;
  const short = l - 0.0894841775 * a - 1.291485548 * b;
  const l3 = long * long * long;
  const m3 = medium * medium * medium;
  const s3 = short * short * short;
  return [
    toSRGB(4.0767416621 * l3 - 3.3077115913 * m3 + 0.2309699292 * s3),
    toSRGB(-1.2684380046 * l3 + 2.6097574011 * m3 - 0.3413193965 * s3),
    toSRGB(-0.0041960863 * l3 - 0.7034186147 * m3 + 1.707614701 * s3),
  ];
}

export function rgbToOKLab(red: number, green: number, blue: number) {
  const r = toLinear(red);
  const g = toLinear(green);
  const b = toLinear(blue);
  const long = Math.cbrt(0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b);
  const medium = Math.cbrt(0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b);
  const short = Math.cbrt(0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b);
  return [
    0.2104542553 * long + 0.793617785 * medium - 0.0040720468 * short,
    1.9779984951 * long - 2.428592205 * medium + 0.4505937099 * short,
    0.0259040371 * long + 0.7827717662 * medium - 0.808675766 * short,
  ];
}

const withinSRGB = (rgb: number[]) =>
  rgb.every((value) => value >= -1e-6 && value <= 1 + 1e-6);

// The most chroma sRGB can show at a lightness and hue, by halving the interval. Reducing chroma is
// how a colour is brought into the gamut: clipping the channels instead would move the hue.
export function gamutChroma(lightness: number, hue: number) {
  const radians = (hue * Math.PI) / 180;
  const cosine = Math.cos(radians);
  const sine = Math.sin(radians);
  let low = 0;
  let high = 0.4; // past anything the gamut reaches
  for (let step = 0; step < 20; step += 1) {
    const middle = 0.5 * (low + high);
    if (withinSRGB(oklabToRGB(lightness, middle * cosine, middle * sine))) {
      low = middle;
    } else {
      high = middle;
    }
  }
  return low;
}

// The colour sRGB can actually show at this lightness and hue: past the gamut edge the chroma stops
// climbing. Asking for more is not an error — it is what the far side of the picker's area is — but
// the answer, and everything measured from it, is this colour.
export function inGamut(color: OKLCh): OKLCh {
  return { ...color, c: Math.min(color.c, gamutChroma(color.l, color.h)) };
}

// An OKLCh colour as 0..255 channels, with the chroma brought inside the gamut first.
export function oklchToRGB({ l, c, h }: OKLCh) {
  const radians = (h * Math.PI) / 180;
  const { c: chroma } = inGamut({ l, c, h });
  return oklabToRGB(l, chroma * Math.cos(radians), chroma * Math.sin(radians)).map(
    (value) => Math.round(clamp01(value) * 255),
  );
}

export function oklchToCSS(color: OKLCh) {
  const [red, green, blue] = oklchToRGB(color);
  return `rgb(${red}, ${green}, ${blue})`;
}
